import { BadgeCheck, FileSignature, Handshake, Lock } from "lucide-react";

export const hiringCards = [
  {
    id: "employment-contract",
    title: "Employment Contract Generator",
    eyebrow: "PAYE / IR35 aware",
    description: "Draft a UK employment contract for an employee or worker, with PAYE, IR35-style off-payroll, or self-employed consultant clauses.",
    path: "/hiring/employment-contract",
    action: "Create contract",
    Icon: FileSignature,
  },
  {
    id: "offer-letter",
    title: "Offer Letter Template",
    eyebrow: "Pre-contract offer",
    description: "Send a structured offer with role, salary, start date, probation, benefits, and acceptance instructions before issuing the full contract.",
    path: "/hiring/offer-letter",
    action: "Draft offer",
    Icon: Handshake,
  },
  {
    id: "emi-grant",
    title: "EMI Equity Grant Letter",
    eyebrow: "UK tax-advantaged",
    description: "Issue an Enterprise Management Incentive (EMI) option grant with vesting schedule, exercise price, and HMRC notice reminders.",
    path: "/hiring/emi-grant",
    action: "Issue grant",
    Icon: BadgeCheck,
  },
  {
    id: "nda",
    title: "NDA Generator",
    eyebrow: "Mutual / one-way",
    description: "Generate a mutual or one-way confidentiality agreement with editable purpose, term, and governing law clauses.",
    path: "/hiring/nda",
    action: "Generate NDA",
    Icon: Lock,
  },
];

export const employmentTypes = {
  employee: {
    label: "Employee (PAYE)",
    summary: "Full UK employee with PAYE income tax, employer NI, statutory sick pay, holiday, and pension auto-enrolment obligations.",
    statusClause: "The Employee is engaged on a contract of service. PAYE income tax and Class 1 National Insurance contributions will be deducted at source. The Employee is entitled to statutory rights including paid annual leave, statutory sick pay, and pension auto-enrolment.",
    irClause: "IR35 does not apply: the Employee is engaged directly under a contract of service, not through an intermediary.",
  },
  insideIr35: {
    label: "Off-payroll worker (Inside IR35)",
    summary: "Contractor working through a limited company / personal service company who has been assessed as inside IR35. Deemed employment for tax — fee payer must operate PAYE.",
    statusClause: "The Worker provides services through a limited company / personal service company. Following an HMRC CEST assessment, the engagement is deemed inside IR35. The fee payer will deduct PAYE income tax and employee National Insurance from the deemed direct payment.",
    irClause: "Inside IR35 applies. A Status Determination Statement (SDS) has been issued. The fee payer will operate PAYE on the deemed direct payment per Chapter 10 ITEPA 2003.",
  },
  outsideIr35: {
    label: "Self-employed / Outside IR35",
    summary: "Genuine self-employed contractor or PSC operating outside IR35. No PAYE or NIC at source — contractor is responsible for their own tax.",
    statusClause: "The Contractor is genuinely self-employed and the engagement falls outside IR35 / off-payroll working rules. The Contractor is responsible for their own income tax, National Insurance, VAT, and any other statutory deductions.",
    irClause: "Outside IR35 / off-payroll: the Contractor is in business on their own account and bears commercial risk, provides their own equipment where reasonable, and may send a substitute under the substitution clause below.",
  },
};

export const defaultEmploymentContract = () => ({
  employmentType: "employee",
  companyName: "Northstar Studio Ltd",
  companyAddress: "1 Practical Lane, London EC1A 1AA",
  companyContact: "people@example.co.uk",
  employeeName: "Candidate Name",
  employeeAddress: "Employee address",
  jobTitle: "Senior Marketing Manager",
  startDate: new Date().toISOString().slice(0, 10),
  workLocation: "Hybrid — London office and remote",
  salary: "£52,000 per annum",
  payFrequency: "Monthly, on the 25th, in arrears",
  hours: "37.5 hours per week, Monday to Friday",
  probation: "3 months",
  noticeEmployer: "1 month after probation",
  noticeEmployee: "1 month after probation",
  holiday: "25 days plus public holidays",
  pension: "Auto-enrolment into the company workplace pension at 5% employee / 3% employer",
  benefits: "Private medical insurance after probation. £500 annual learning budget.",
  confidentiality: "The Employee shall not disclose confidential information during or after employment.",
  ip: "All intellectual property created in the course of employment is assigned to the Company.",
  postTermination: "6-month non-solicit of clients and employees within the UK.",
  governingLaw: "England and Wales",
  signatureDate: new Date().toISOString().slice(0, 10),
});

export const defaultOfferLetter = () => ({
  companyName: "Northstar Studio Ltd",
  companyAddress: "1 Practical Lane, London EC1A 1AA",
  signerName: "Founder Name",
  signerTitle: "Founder",
  candidateName: "Candidate Name",
  candidateAddress: "Candidate address",
  jobTitle: "Senior Marketing Manager",
  reportingTo: "Founder",
  startDate: new Date().toISOString().slice(0, 10),
  salary: "£52,000 per annum",
  bonus: "Annual performance bonus up to 10% of base salary, subject to company performance.",
  equity: "EMI option grant equivalent to 0.5% of fully-diluted equity, vesting over 4 years with a 1-year cliff.",
  benefits: "25 days holiday, pension auto-enrolment, private medical insurance after probation.",
  probation: "3 months",
  workLocation: "Hybrid — London office 2 days per week",
  responseDeadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
  conditions: "This offer is conditional on satisfactory references, right to work in the UK, and signing the Company's standard employment contract.",
  closingNote: "We are excited about the contribution you can make and look forward to your decision.",
});

export const defaultEmiGrant = () => ({
  companyName: "Northstar Studio Ltd",
  companyNumber: "12345678",
  companyAddress: "1 Practical Lane, London EC1A 1AA",
  optionholderName: "Employee Name",
  optionholderAddress: "Employee address",
  grantDate: new Date().toISOString().slice(0, 10),
  numberOfShares: 12000,
  shareClass: "Ordinary shares of £0.0001 each",
  exercisePrice: "£0.18 per share (HMRC-agreed market value as of grant date)",
  totalExerciseValue: "£2,160",
  vestingSchedule: "4 years total, with a 1-year cliff (25% on the first anniversary), then monthly vesting in equal instalments of 1/48 over the remaining 36 months.",
  exerciseWindow: "Options must be exercised within 90 days of leaving the Company in good leaver circumstances; otherwise they lapse.",
  exitOnly: false,
  hmrcNoticeNote: "The Company will notify HMRC of the EMI option grant within 92 days using the ERS online service. Failure to notify means the option will not qualify for EMI tax treatment.",
  rulesReference: "This grant is subject to the rules of the Northstar Studio Ltd EMI Share Option Plan dated as referenced in the option certificate.",
  signerName: "Founder Name",
  signerTitle: "Director",
});

export const defaultNda = () => ({
  ndaType: "mutual",
  effectiveDate: new Date().toISOString().slice(0, 10),
  partyOneName: "Northstar Studio Ltd",
  partyOneAddress: "1 Practical Lane, London EC1A 1AA",
  partyTwoName: "Counterparty Ltd",
  partyTwoAddress: "Counterparty address",
  purpose: "Discussions regarding a potential commercial collaboration including product, technology, and customer information.",
  termYears: 2,
  governingLaw: "England and Wales",
  excludedInformation: "Information already in the public domain through no fault of the receiving party, independently developed without reference to the disclosed information, or rightfully received from a third party without obligation of confidence.",
  permittedDisclosures: "To employees, contractors, and professional advisers on a need-to-know basis, who are bound by equivalent confidentiality obligations.",
  signerOneName: "Founder Name",
  signerOneTitle: "Director",
  signerTwoName: "Counterparty Signer",
  signerTwoTitle: "Director",
});
